import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { resetPublicSiteContentCache } from '@/hooks/useSiteContent';
import { siteContentService, type PricingSettings } from '@/services/site-content.service';
import { classNames } from '@/utils/classNames';

import { AdminPromoCodesSection } from './AdminPromoCodesSection';
import styles from './AdminPricingSection.module.css';

type PricingTab = 'promoCodes' | 'markup';

function pricingTabFromPathname(pathname: string): PricingTab {
  if (pathname === '/admin/pricing/markup') {
    return 'markup';
  }

  return 'promoCodes';
}

function clampMarginPercent(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.max(0, Math.min(100, Math.trunc(value)));
}

function normalizePricing(pricing: PricingSettings): PricingSettings {
  return {
    marginPercent: clampMarginPercent(pricing.marginPercent),
    mamMaxPercent: clampMarginPercent(pricing.mamMaxPercent),
    mamGuestEarnEnabled: pricing.mamGuestEarnEnabled === true,
  };
}

function serializeMarkupState(pricing: PricingSettings, fixedPriceEmailsText: string): string {
  return JSON.stringify({
    pricing: normalizePricing(pricing),
    fixedPriceEmails: parseEmailList(fixedPriceEmailsText),
  });
}

/** One email per line (commas, semicolons and spaces also separate); lower-cased, de-duplicated. */
function parseEmailList(text: string): string[] {
  const emails = text
    .split(/[\s,;]+/)
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0);
  return [...new Set(emails)].sort();
}

const AdminMarkupSection = () => {
  const [draft, setDraft] = useState<PricingSettings | null>(null);
  const [fixedPriceEmailsText, setFixedPriceEmailsText] = useState('');
  const [savedFingerprint, setSavedFingerprint] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [announceSaveToken, setAnnounceSaveToken] = useState(0);

  useEffect(() => {
    void Promise.all([
      siteContentService.getAdminPricingSettings(),
      siteContentService.getAdminFixedPriceEmails(),
    ])
      .then(([pricing, fixedPriceEmails]) => {
        const normalizedPricing = normalizePricing(pricing);
        const emailsText = fixedPriceEmails.join('\n');
        setDraft(normalizedPricing);
        setFixedPriceEmailsText(emailsText);
        setSavedFingerprint(serializeMarkupState(normalizedPricing, emailsText));
        setLoadError(null);
      })
      .catch((err: unknown) => {
        setLoadError(err instanceof Error ? err.message : 'Failed to load pricing settings');
      });
  }, []);

  const draftFingerprint = useMemo(
    () => (draft !== null ? serializeMarkupState(draft, fixedPriceEmailsText) : ''),
    [draft, fixedPriceEmailsText],
  );

  const hasUnsavedChanges =
    savedFingerprint !== null && draft !== null && draftFingerprint !== savedFingerprint;

  useEffect(() => {
    if (announceSaveToken === 0) {
      return;
    }

    const t = window.setTimeout(() => {
      setAnnounceSaveToken(0);
    }, 4500);

    return () => {
      window.clearTimeout(t);
    };
  }, [announceSaveToken]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const handleSave = useCallback(async () => {
    if (!draft) {
      return;
    }

    setSaveError(null);
    setIsSaving(true);
    try {
      const [updatedPricing, updatedEmails] = await Promise.all([
        siteContentService.patchAdminPricingSettings(normalizePricing(draft)),
        siteContentService.patchAdminFixedPriceEmails(parseEmailList(fixedPriceEmailsText)),
      ]);
      const normalizedPricing = normalizePricing(updatedPricing);
      const emailsText = updatedEmails.join('\n');
      setDraft(normalizedPricing);
      setFixedPriceEmailsText(emailsText);
      setSavedFingerprint(serializeMarkupState(normalizedPricing, emailsText));
      setAnnounceSaveToken(Date.now());
      resetPublicSiteContentCache();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  }, [draft, fixedPriceEmailsText]);

  if (loadError && !draft) {
    return (
      <div className={styles.wrap}>
        <p className={styles.error}>{loadError}</p>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={() => {
            window.location.reload();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!draft) {
    return <p className={styles.muted}>Loading pricing settings...</p>;
  }

  return (
    <div className={styles.wrap}>
      {(hasUnsavedChanges || announceSaveToken !== 0) && (
        <div className={styles.alertDock}>
          <div className={styles.alertDockInner}>
            {hasUnsavedChanges ? (
              <>
                <p className={styles.unsavedNote}>
                  You have unsaved changes. Save before leaving if you want to keep them.
                </p>
                <button
                  type="button"
                  className={classNames(styles.primaryBtn, styles.dockPrimaryBtn)}
                  disabled={isSaving || !hasUnsavedChanges}
                  onClick={() => {
                    void handleSave();
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save changes'}
                </button>
              </>
            ) : (
              <p className={styles.savedNote} role="status" aria-live="polite">
                Changes saved successfully.
              </p>
            )}
          </div>
        </div>
      )}

      <h2 className={styles.heading}>Markup</h2>
      <section className={styles.block}>
        <label className={styles.label}>
          Range: 0-100
          <input
            className={styles.input}
            type="number"
            min={0}
            max={100}
            step={1}
            value={draft.marginPercent}
            onChange={(e) => {
              const parsed = Number.parseInt(e.target.value, 10);
              setDraft((prev) =>
                prev
                  ? {
                      ...prev,
                      marginPercent: clampMarginPercent(Number.isNaN(parsed) ? 0 : parsed),
                    }
                  : prev,
              );
            }}
          />
        </label>
        <p className={styles.muted}>
          Applied to incoming supplier prices before promo codes are calculated.
        </p>
      </section>

      <h2 className={styles.heading}>Loyalty Points Maximum Cap</h2>
      <section className={styles.block}>

        <label className={styles.label}>
          Range: 0-100
          <input
            className={styles.input}
            type="number"
            min={0}
            max={100}
            step={1}
            value={draft.mamMaxPercent}
            onChange={(e) => {
              const parsed = Number.parseInt(e.target.value, 10);
              setDraft((prev) =>
                prev
                  ? {
                      ...prev,
                      mamMaxPercent: clampMarginPercent(Number.isNaN(parsed) ? 0 : parsed),
                    }
                  : prev,
              );
            }}
          />
        </label>
        <p className={styles.muted}>
          Maximum share of order total that can be paid with MAM. When set to 0, MAM feature is disabled.
        </p>
      </section>

      <h2 className={styles.heading}>Loyalty Points for Guests</h2>
      <section className={styles.block}>
        <label className={styles.label}>
          <input
            type="checkbox"
            checked={draft.mamGuestEarnEnabled}
            onChange={(e) => {
              const checked = e.target.checked;
              setDraft((prev) => (prev ? { ...prev, mamGuestEarnEnabled: checked } : prev));
            }}
          />{' '}
          Not logged-in customers earn MAM
        </label>
        <p className={styles.muted}>
          When on, a paid order without an A2A login is matched to an A2A account by the buyer email, and a
          registered loyalty programme member on MAM Engine is credited. Using points still requires the login.
          When off, only customers who log in at checkout earn. Ski pass purchases earn on MAM Engine only:
          customers still on the legacy A2A wallet do not earn here, logged in or not.
        </p>
      </section>

      <h2 className={styles.heading}>€1 Orders for A2A Accounts</h2>
      <section className={styles.block}>
        <label className={styles.label}>
          A2A account emails, one per line
          <textarea
            className={styles.input}
            rows={5}
            spellCheck={false}
            placeholder="name@example.com"
            value={fixedPriceEmailsText}
            onChange={(e) => {
              setFixedPriceEmailsText(e.target.value);
            }}
          />
        </label>
        <p className={styles.muted}>
          The whole order costs 1 (in the order currency) only while one of these accounts is logged in to
          Alps2Alps loyalty at checkout. An email typed into the booking form does not count. Passes are still
          bought at the full supplier price. Leave empty to turn this off.
        </p>
      </section>

      {saveError ? <p className={styles.error}>{saveError}</p> : null}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primaryBtn}
          disabled={isSaving || !hasUnsavedChanges}
          onClick={() => {
            void handleSave();
          }}
        >
          {isSaving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

export const AdminPricingSection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<PricingTab>(() => pricingTabFromPathname(location.pathname));

  useEffect(() => {
    setActiveTab(pricingTabFromPathname(location.pathname));
  }, [location.pathname]);

  return (
    <div>
      <div className={styles.tabs}>
        <button
          type="button"
          className={classNames(styles.tab, activeTab === 'promoCodes' ? styles.tabActive : styles.tabMuted)}
          onClick={() => {
            setActiveTab('promoCodes');
            void navigate('/admin/promocodes');
          }}
        >
          Promo codes
        </button>
        <button
          type="button"
          className={classNames(styles.tab, activeTab === 'markup' ? styles.tabActive : styles.tabMuted)}
          onClick={() => {
            setActiveTab('markup');
            void navigate('/admin/pricing/markup');
          }}
        >
          Rates
        </button>
      </div>

      <section className={styles.section}>
        {activeTab === 'promoCodes' ? <AdminPromoCodesSection /> : null}
        {activeTab === 'markup' ? <AdminMarkupSection /> : null}
      </section>
    </div>
  );
};
